import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Copy, ExternalLink, FolderPlus, HardDrive,
  Loader2, LogOut, Plug, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/auth";
import { toast } from "sonner";

export const DriveManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [authUrl, setAuthUrl] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/drive/status");
      setData(res.data);
    } catch (e) {
      toast.error("No se pudo consultar el estado de Google Drive.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Vuelta desde la pantalla de permisos de Google
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const drive = params.get("drive");
    if (!drive) return;
    if (drive === "ok") toast.success("Google Drive conectado.");
    else if (drive === "scope") toast.error("Faltan permisos: acepta el acceso a Drive.");
    else if (drive === "state") toast.error("La conexión caducó. Vuelve a intentarlo.");
    else {
      const detail = params.get("detail");
      toast.error(detail ? `Google: ${detail}` : "Google rechazó la conexión.",
        { duration: 12000 });
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await adminApi.get("/admin/drive/connect");
      const url = res.data.authorization_url;
      setAuthUrl(url);
      // Google no admite su pantalla de permisos dentro de una vista
      // incrustada: se abre siempre en una pestaña nueva del navegador.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) toast.info("Permite las ventanas emergentes o usa el enlace de abajo.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión.");
    } finally {
      setConnecting(false);
    }
  };

  useEffect(() => {
    if (!authUrl) return;
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [authUrl, load]);

  const disconnect = async () => {
    try {
      await adminApi.post("/admin/drive/disconnect");
      toast.success("Cuenta de Google desconectada.");
      await load();
    } catch (e) {
      toast.error("No se pudo desconectar.");
    }
  };

  const backfill = async () => {
    setWorking(true);
    try {
      const res = await adminApi.post("/admin/drive/backfill");
      const started = res.data?.started || 0;
      if (!started) toast.info("Todos los cursos ya tienen su carpeta.");
      else {
        toast.success(
          `Creando ${started} carpeta(s) en Drive. Tardará un par de minutos.`,
          { duration: 8000 }
        );
        // Refresco periódico mientras el servidor las va creando
        let rounds = 0;
        const timer = setInterval(async () => {
          rounds += 1;
          await load();
          if (rounds >= 20) clearInterval(timer);
        }, 6000);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron crear las carpetas.");
    } finally {
      setWorking(false);
    }
  };

  const retry = async (courseId) => {
    try {
      const res = await adminApi.post(`/admin/courses/${courseId}/drive`);
      toast.success(res.data?.folder ? `Carpeta creada: ${res.data.folder}` : "Listo.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la carpeta.");
    }
  };

  const uploadKey = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.post("/admin/drive/credentials", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Clave guardada: ${res.data?.service_account}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la clave.");
    } finally {
      setUploading(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(data?.service_account || "");
      toast.success("Correo del robot copiado.");
    } catch {
      toast.error("Copia el correo a mano.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Consultando Google Drive…
      </div>
    );
  }

  const ok = data?.uploads_enabled && data?.root_found;

  return (
    <section
      className="rounded-sm border-2 border-zinc-200 bg-white p-4"
      data-testid="drive-manager"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-zinc-950">
            <HardDrive className="h-5 w-5 text-zinc-400" />
            Carpetas en Google Drive
          </h3>
          <p className="mt-1 text-sm text-zinc-600">
            Cada curso nuevo recibe su carpeta en{" "}
            <span className="font-mono text-zinc-900">FORMACION / año / comunidad</span>{" "}
            con el número correlativo siguiente. En <b>Asturias</b> y <b>Murcia</b>{" "}
            además se sube la Memoria Previa y se deja el borrador para la
            Consejería en el buzón de formación.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data?.uploads_enabled ? (
            <Button
              variant="outline"
              onClick={disconnect}
              className="h-9 border-2 border-zinc-300 text-xs font-semibold"
              data-testid="drive-disconnect"
            >
              <LogOut className="mr-1.5 h-3.5 w-3.5" />
              Desconectar
            </Button>
          ) : (
            <Button
              onClick={connect}
              disabled={connecting}
              className="h-9 bg-[#1a73e8] text-xs font-semibold text-white hover:bg-[#1765cc]"
              data-testid="drive-connect"
            >
              {connecting ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Abriendo Google…</>
              ) : (
                <><Plug className="mr-1.5 h-3.5 w-3.5" />Conectar Google Drive</>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={load}
            className="h-9 border-2 border-zinc-300 text-xs font-semibold"
            data-testid="drive-refresh"
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Actualizar
          </Button>
          <Button
            onClick={backfill}
            disabled={working || !data?.configured || !data?.root_found}
            className="h-9 bg-zinc-950 text-xs font-semibold hover:bg-zinc-800"
            data-testid="drive-backfill"
          >
            {working ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Creando…</>
            ) : (
              <><FolderPlus className="mr-1.5 h-3.5 w-3.5" />Crear las que falten</>
            )}
          </Button>
        </div>
      </div>

      <div
        className={`mt-3 rounded-sm border-2 px-3 py-2 text-sm ${
          ok ? "border-emerald-300 bg-emerald-50 text-emerald-900"
             : "border-amber-300 bg-amber-50 text-amber-900"
        }`}
        data-testid="drive-status"
      >
        <div className="flex items-start gap-2">
          {ok ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="font-semibold">{data?.detail}</p>
            {data?.google_account && (
              <p
                className="mt-1 text-xs"
                data-testid="drive-google-account"
              >
                Cuenta de Google: <b>{data.google_account}</b>
              </p>
            )}
            {authUrl && !data?.uploads_enabled && (
              <p className="mt-2 text-xs">
                ¿No se ha abierto la ventana de Google?{" "}
                <a
                  href={authUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-bold underline"
                  data-testid="drive-auth-link"
                >
                  abre este enlace en una pestaña nueva
                </a>
                .
              </p>
            )}
            {!data?.uploads_enabled && (
              <p className="mt-2 text-xs">
                Para que los documentos se suban a la carpeta del curso hay que{" "}
                <b>conectar tu cuenta de Google</b>: pulsa «Conectar Google
                Drive», elige la cuenta donde está la carpeta <b>FORMACION</b>{" "}
                y acepta el acceso. Google avisará de que la aplicación no está
                verificada: entra en <i>Configuración avanzada → Ir a DESFIEMER
                CAMPUS</i>.
              </p>
            )}
            {data?.service_account && !data?.uploads_enabled && (
              <p className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                Robot (solo carpetas):{" "}
                <span className="font-mono">{data.service_account}</span>
                <button
                  type="button"
                  onClick={copyEmail}
                  className="inline-flex items-center gap-1 rounded-sm border border-current px-1.5 py-0.5 font-semibold"
                  data-testid="drive-copy-email"
                >
                  <Copy className="h-3 w-3" /> copiar
                </button>
              </p>
            )}
            {!data?.service_account && !data?.uploads_enabled && (
              <input
                type="file"
                accept=".json,application/json"
                disabled={uploading}
                onChange={(e) => uploadKey((e.target.files || [])[0])}
                className="mt-2 block w-full max-w-md cursor-pointer rounded-sm border-2 border-amber-300 bg-white p-2 text-xs file:mr-3 file:rounded-sm file:border-0 file:bg-zinc-950 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white"
                data-testid="drive-upload-key"
              />
            )}
            {(data?.regions || []).length > 0 && (
              <p className="mt-1 text-xs">
                Comunidades en Drive: {(data.regions || []).join(" · ")}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {[
          ["Cursos con carpeta", data?.courses_with_folder ?? 0, "text-emerald-700"],
          ["Sin carpeta", data?.courses_pending ?? 0, "text-amber-700"],
          ["Con error", (data?.failed || []).length, "text-red-700"],
        ].map(([label, value, cls]) => (
          <div key={label} className="rounded-sm border border-zinc-200 px-3 py-2">
            <div className={`text-xl font-extrabold tabular-nums ${cls}`}>{value}</div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
              {label}
            </div>
          </div>
        ))}
      </div>

      {(data?.failed || []).length > 0 && (
        <div className="mt-3 space-y-1.5">
          {(data.failed || []).map((c) => (
            <div
              key={c.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs"
              data-testid={`drive-failed-${c.id}`}
            >
              <div className="min-w-0">
                <p className="font-bold text-red-900">{c.name}</p>
                <p className="text-red-700">{c.drive_error}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => retry(c.id)}
                className="h-8 border-2 border-red-300 text-xs font-semibold"
                data-testid={`drive-retry-${c.id}`}
              >
                Reintentar
              </Button>
            </div>
          ))}
        </div>
      )}

      {(data?.recent || []).length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            Últimas carpetas creadas
          </p>
          <ul className="space-y-1">
            {(data.recent || []).map((c) => (
              <li
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-zinc-200 px-3 py-1.5 text-xs"
              >
                <span className="font-mono text-zinc-900">{c.drive_folder_name}</span>
                <span className="flex items-center gap-2">
                  {c.previa_status === "ok" && (
                    <span className="rounded-sm border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-800">
                      Previa + borrador
                    </span>
                  )}
                  {c.previa_status === "error" && (
                    <span
                      className="rounded-sm border border-red-300 bg-red-50 px-1.5 py-0.5 font-semibold text-red-800"
                      title={c.previa_detail}
                    >
                      Previa con error
                    </span>
                  )}
                  <a
                    href={c.drive_folder_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-zinc-700 hover:underline"
                    data-testid={`drive-link-${c.id}`}
                  >
                    abrir <ExternalLink className="h-3 w-3" />
                  </a>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default DriveManager;
